import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
} from '@mui/material';
import { useDivisionOptions } from '../../../state/master';
import ReactDOM from 'react-dom';

// ローカル定義: 汎用Option型
type Option = { value: string; label: string };

// 軽量版 CheckboxDropdownSelect（複数選択・チェックボックス式）
const CheckboxDropdownSelect: React.FC<{
  id: string;
  placeholder?: string;
  options: Option[];
  value: Option[];
  onChange: (next: Option[]) => void;
  disabled?: boolean;
}> = ({ id, placeholder, options, value, onChange, disabled }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);

  const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();
  const uniqueOptions = (opts: Option[]) => {
    const seen = new Set<string>();
    return (opts ?? []).filter((o) => {
      const k = `${o.value}__${o.label}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  const opts = React.useMemo(() => uniqueOptions(options), [options]);
  const selected = React.useMemo(
    () => (Array.isArray(value) ? value.map((v) => ({ value: norm(v.value), label: norm(v.label) })) : []),
    [value]
  );
  const selectedSet = React.useMemo(() => new Set(selected.map((v) => v.value)), [selected]);

  // ポータル用のメニュー参照と、トリガーボタン（アンカー）参照
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const btnRef = React.useRef<HTMLButtonElement | null>(null);

  // メニューの表示位置（Viewport基準・ダイアログ外にもはみ出して表示できるように）
  const [pos, setPos] = React.useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  const updatePosition = React.useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // メニューの幅はボタン幅以上に（最低260px）
    const width = Math.max(r.width, 260);
    // 横方向のはみ出しを抑制
    let left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));

    // 簡易な縦方向のフリップ（下に入らなければ上に展開）
    const menuMaxH = 260; // メニューの最大高さ（スタイルと合わせる）
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;

    let top = r.bottom + 4; // 下に出す
    if (spaceBelow < menuMaxH + 8 && spaceAbove >= menuMaxH + 8) {
      top = r.top - menuMaxH - 4; // 上に出す
    }
    // ビューポートに収まるよう最終クランプ
    top = Math.max(8, Math.min(top, window.innerHeight - 8 - 48));

    setPos({ top, left, width });
  }, []);

  // 外側クリックでメニューを閉じる（ポータル側の要素も考慮）
  React.useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      const el = e.target as Node | null;
      const inButton = !!(btnRef.current && el && btnRef.current.contains(el));
      const inRoot = !!(rootRef.current && el && rootRef.current.contains(el));
      const inMenu = !!(menuRef.current && el && menuRef.current.contains(el));
      if (!(inButton || inRoot || inMenu)) setOpen(false);
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('touchstart', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('touchstart', handler, true);
    };
  }, []);

  // 開いたら位置算出。スクロール/リサイズで追従
  React.useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  const apply = (vals: string[]) => {
    const map = new Map(opts.map((o) => [o.value, o.label]));
    onChange(vals.map((v) => ({ value: v, label: map.get(v) ?? v })));
  };

  const toggleOne = (v: string) => {
    const s = new Set(selectedSet);
    s.has(v) ? s.delete(v) : s.add(v);
    apply(Array.from(s));
  };

  const allSelected = opts.length > 0 && opts.every((o) => selectedSet.has(o.value));
  const someSelected = !allSelected && opts.some((o) => selectedSet.has(o.value));
  const toggleAll = () => {
    if (allSelected) {
      apply([]);
    } else {
      apply(opts.map((o) => o.value));
    }
  };

  const summary = (() => {
    if (selectedSet.size === 0) return placeholder ?? '';
    if (opts.length > 0 && selectedSet.size === opts.length) return 'すべて';
    const labels = opts.filter((o) => selectedSet.has(o.value)).map((o) => o.label);
    return labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} 他${labels.length - 2}`;
  })();

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        ref={btnRef}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          width: '100%',
          minHeight: 36,
          padding: '6px 10px',
          borderRadius: 8,
          border: isDark ? '1px solid #334155' : '1px solid #ddd',
          background: disabled
            ? (isDark ? '#263147' : '#f5f5f5')
            : (isDark ? '#1e293b' : 'white'),
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: isDark ? '#e2e8f0' : '#111',
        }}
      >
        <span style={{ color: summary ? (isDark ? '#e2e8f0' : '#111') : (isDark ? '#64748b' : '#999') }}>{summary || placeholder || ''}</span>
        <span style={{ float: 'right', opacity: 0.7 }}>▼</span>
      </button>

      {open && !disabled && ReactDOM.createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'fixed',
            zIndex: 1400,
            top: `${pos.top}px`,
            left: `${pos.left}px`,
            minWidth: `${pos.width}px`,
            maxHeight: 260,
            overflowY: 'auto',
            border: isDark ? '1px solid #334155' : '1px solid #ddd',
            borderRadius: 8,
            background: isDark ? '#1e293b' : 'white',
            color: isDark ? '#e2e8f0' : '#111',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            padding: 8
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px',
              borderRadius: 6,
              fontWeight: 600
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={toggleAll}
            />
            <span>すべて</span>
          </label>

          <div style={{ marginTop: 6 }}>
            {opts.map((o) => {
              const checked = selectedSet.has(o.value);
              return (
                <label
                  key={`${id}__${o.value}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 6,
                    background: checked
                      ? (isDark ? '#243044' : '#f2f7ff')
                      : 'transparent',
                    cursor: 'pointer'
                  }}
                  aria-selected={checked}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleOne(o.value)} />
                  <span>{o.label}</span>
                </label>
              );
            })}
            {opts.length === 0 && (
              <div style={{ padding: '6px 8px', color: isDark ? '#94a3b8' : '#777', fontSize: 12 }}>候補がありません</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** 行コンテキストの表示用（対象名など） */
  targetLabel?: string;
  /** /get_attributes の options[i].public をそのまま渡す */
  initialPublic?: {
    private_flg?: number | string | null;
    public_department?: string | null;
    public_team?: string | null;
  } | null;
  /** ダイアログを閉じるタイミングで、UI状態を親へ適用 */
  onApply?: (next: { private_flg: number; public_department: string | null; public_team: string | null }) => void;
};

const PublishScopeDialog: React.FC<Props> = ({ open, onClose, targetLabel, initialPublic, onApply }) => {
  const { divisionOptions } = useDivisionOptions();

  const departmentOptions: Option[] = React.useMemo(() => {
    const list = Array.isArray(divisionOptions) ? divisionOptions : [];
    return list.map((d: any) => ({
      value: String((d as any)?.department_id ?? (d as any)?.id ?? ''),
      label: String((d as any)?.label ?? (d as any)?.value ?? ''),
    }));
  }, [divisionOptions]);

  // 公開状態: 'public' | 'private' | 'partial'
  const [mode, setMode] = React.useState<'public' | 'private' | 'partial'>('public');
  // 一部公開: 選択された部門（複数）
  const [selectedDepts, setSelectedDepts] = React.useState<Option[]>([]);

  const handleChangeMode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = String(e.target.value);
    if (v === 'public' || v === 'private' || v === 'partial') {
      setMode(v);
    }
  };

  // 初期反映はダイアログごとに1回だけ行うためのフラグ
  const initRef = React.useRef(false);

  // 初期値を backend の public 設定から反映（ダイアログを開いたタイミングで1回のみ）
  React.useEffect(() => {
    if (!open) {
      // 次回オープン時のために初期化フラグを戻す
      initRef.current = false;
      return;
    }
    if (initRef.current) return;

    const privRaw = (initialPublic as any)?.private_flg;
    const priv = Number(privRaw);

    if (priv === 1) setMode('private');
    else if (priv === 2) setMode('partial');
    else setMode('public');

    // 一部公開の場合、public_department のコード群から選択状態を復元
    const pubDep = String((initialPublic as any)?.public_department ?? '').trim();
    const list = Array.isArray(divisionOptions) ? divisionOptions : [];

    // divisionOptions が未ロードの間は初期反映を保留
    if (priv === 2 && list.length === 0) {
      return;
    }

    if (priv === 2 && pubDep) {
      const codes = pubDep.split(',').map(s => String(s || '').trim()).filter(Boolean);
      const seen = new Set<string>();
      const picks: Option[] = [];

      // divisionOptions からコード一致(ラベル or 値)で引き当て
      codes.forEach(code => {
        const found = list.find((d: any) => {
          const lab = String((d as any)?.label ?? '').trim();
          const val = String((d as any)?.value ?? '').trim();
          return lab === code || val === code;
        });
        if (found) {
          const depId = String((found as any)?.department_id ?? (found as any)?.id ?? '');
          if (depId && !seen.has(depId)) {
            picks.push({
              value: depId,
              label: String((found as any)?.label ?? (found as any)?.value ?? depId)
            });
            seen.add(depId);
          }
        }
      });
      setSelectedDepts(picks);
    } else {
      setSelectedDepts([]);
    }

    // 初期反映は1回のみ
    initRef.current = true;
  }, [open, initialPublic, divisionOptions]);
 
  const buildPublicPayload = React.useCallback(() => {
    const priv = mode === 'private' ? 1 : (mode === 'partial' ? 2 : 0);
    let depCodes: string[] = [];
    if (mode === 'partial') {
      const list = Array.isArray(divisionOptions) ? divisionOptions : [];
      const byId = new Map<string, any>(list.map((d: any) => [String((d as any)?.department_id ?? (d as any)?.id ?? ''), d]));
      depCodes = (selectedDepts ?? [])
        .map((o) => {
          const found = byId.get(String(o.value));
          const code = String((found as any)?.value ?? (found as any)?.label ?? '').trim();
          return code;
        })
        .filter(Boolean);
    }
    return {
      private_flg: priv,
      public_department: depCodes.length > 0 ? depCodes.join(',') : null,
      public_team: null,
    };
  }, [mode, selectedDepts, divisionOptions]);
 
  const handleApplyAndClose = () => {
    try {
      if (typeof onApply === 'function') {
        onApply(buildPublicPayload());
      }
    } finally {
      onClose();
    }
  };

  const handleDialogClose = (_event?: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') return;
    onClose();
  };
 
  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        他課公開の設定
      </DialogTitle>
      <DialogContent dividers>
        {targetLabel && (
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            対象: <b>{targetLabel}</b>
          </Typography>
        )}

        <RadioGroup row name="publish-scope" value={mode} onChange={handleChangeMode} sx={{ mb: 1 }}>
          <FormControlLabel value="public" control={<Radio />} label="公開" />
          <FormControlLabel value="private" control={<Radio />} label="非公開" />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel value="partial" control={<Radio />} label="一部公開" />
            <Box sx={{ minWidth: 280, ml: 1 }}>
              <CheckboxDropdownSelect
                id="partial-departments"
                placeholder="-- 部門を選択 --"
                options={departmentOptions}
                value={selectedDepts}
                onChange={setSelectedDepts}
                disabled={mode !== 'partial'}
              />
            </Box>
          </Box>
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={handleDialogClose}>キャンセル</Button>
        <Button variant="contained" onClick={handleApplyAndClose}>設定する</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishScopeDialog;